import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useConfigContext } from '#src/contexts/config/index.ts';

import type { useCardSetBranding_cardSet } from './__generated__/useCardSetBranding.graphql.ts';

/**
 * Every card-set branding asset URL read from the ConfigQuery. Use for chrome
 * that has no closer query of its own; components with a nearby query should
 * select `assetUrl(slot: …)` and resolve the URL themselves.
 *
 * `colorTheme`, `referralBannerUrl`, `setBackgroundUrl`, `setIconUrl`,
 * `setLogoUrl`, `surfacePrimaryColor`, `surfaceSecondaryTextColor` and the
 * squad illustrations fall back to the next set, which is the only branding
 * available while a set transition is in flight.
 */
export const useCardSetBranding = () => {
  const { cardSet } = useConfigContext();
  const branding = cardSet?.currentSet?.branding;
  const nextBranding = cardSet?.nextSet?.branding;
  const transitioningBranding = branding ?? nextBranding;

  return {
    colorTheme: branding?.colorTheme ?? nextBranding?.colorTheme,
    composeLineupBackgroundUrl: branding?.composeLineup,
    composeLineupTrainingBackgroundUrl: branding?.composeLineupTraining,
    gemsBackgroundDesktopUrl: branding?.gemsBackgroundDesktop ?? undefined,
    gemsBackgroundMobileUrl: branding?.gemsBackgroundMobile ?? undefined,
    lineupBackgroundUrl: branding?.lineupBackground,
    marketBannerDesktopUrl: branding?.marketBannerDesktop ?? undefined,
    marketBannerMobileUrl: branding?.marketBannerMobile ?? undefined,
    referralBannerUrl: transitioningBranding?.referralBanner ?? undefined,
    rewardSuccessPopupBackgroundUrl: branding?.rewardSuccessPopupMobile,
    setBackgroundUrl: branding?.setBackground ?? nextBranding?.setBackground,
    setIconUrl: branding?.setIcon ?? nextBranding?.setIcon,
    setLogoUrl: branding?.setLogo ?? nextBranding?.setLogo,
    squadBoardUrl: transitioningBranding?.board ?? undefined,
    squadSplashUrl: transitioningBranding?.splash ?? undefined,
    surfacePrimaryColor:
      branding?.colors?.surfacePrimary ?? nextBranding?.colors?.surfacePrimary,
    surfaceSecondaryTextColor:
      branding?.colors?.surfaceSecondaryText ??
      nextBranding?.colors?.surfaceSecondaryText,
    welcomeOfferEntryPointUrl: branding?.welcomeOfferEntryPoint ?? undefined,
  };
};

useCardSetBranding.fragments = {
  cardSet: gql`
    fragment useCardSetBranding_cardSet on CardSet {
      slug
      branding {
        id
        board: assetUrl(slot: BRANDING_HOMEPAGE_ASSET_SQUAD_MOBILE)
        colors {
          surfacePrimary
          surfaceSecondaryText
        }
        colorTheme
        composeLineup: assetUrl(slot: PLAY_COMPOSE_LINEUP_DESKTOP)
        composeLineupTraining: assetUrl(
          slot: PLAY_COMPOSE_LINEUP_TRAINING_DESKTOP
        )
        gemsBackgroundDesktop: assetUrl(slot: MARKET_GEMS_BACKGROUND_DESKTOP)
        gemsBackgroundMobile: assetUrl(slot: MARKET_GEMS_BACKGROUND_MOBILE)
        lineupBackground: assetUrl(slot: PLAY_COMPOSE_LINEUP_LINEUP_BACKGROUND)
        marketBannerDesktop: assetUrl(slot: MARKET_BANNER_DESKTOP)
        marketBannerMobile: assetUrl(slot: MARKET_BANNER_MOBILE)
        referralBanner: assetUrl(slot: BRANDING_REFERRAL_BANNER_MOBILE)
        rewardSuccessPopupMobile: assetUrl(
          slot: PLAY_REWARD_SUCCES_POPUP_MOBILE
        )
        setBackground: assetUrl(slot: PLAY_LINEUP_VIEW_DESKTOP)
        setIcon: assetUrl(slot: BRANDING_SET_ICON_MOBILE)
        setLogo: assetUrl(slot: BRANDING_SET_LOGO_COMPOSITE_MOBILE)
        splash: assetUrl(slot: BRANDING_BOARD_SQUAD_SPLASH)
        welcomeOfferEntryPoint: assetUrl(
          slot: MARKET_WELCOME_OFFER_MARKET_ENTRY_POINT
        )
      }
    }
  ` as TypedDocumentNode<useCardSetBranding_cardSet>,
};
